import { compact, intersection, isNil, map } from 'lodash';
import { IBaseDishModalFormState } from './BaseDishModal';
import { DEFAULT_CUSTOMIZATION_ID } from './types';

interface CustomizableDish {
  servingSizeCustomizations?: { id: string }[] | null;
  spiceLevelCustomizations?: { id: string }[] | null;
}

interface InitialDishModalFormStateParams {
  dish: CustomizableDish;
  /** The customizations on the dish's line item, if it has one. */
  customizationIds: string[] | null | undefined;
  quantityInCart: number | null | undefined;
  /**
   * Whether the shef's default portion starts selected for a dish not yet in the cart. Off, the
   * eater has to pick a portion before adding it.
   */
  preselectDefaultServingSize?: boolean;
}

const customizationIdAmong = (
  customizationIds: string[] | null | undefined,
  customizations: { id: string }[] | null | undefined
): string | undefined =>
  isNil(customizationIds) || isNil(customizations)
    ? undefined
    : intersection(customizationIds, map(customizations, 'id'))[0];

/** The dish modal's opening form state for a dish, reflecting its line item when it is already in the cart. */
export const initialDishModalFormState = ({
  dish: { servingSizeCustomizations, spiceLevelCustomizations },
  customizationIds,
  quantityInCart,
  preselectDefaultServingSize = false,
}: InitialDishModalFormStateParams): IBaseDishModalFormState => {
  const isInCart = !isNil(quantityInCart) && quantityInCart > 0;
  const servingSizeCustomizationId =
    customizationIdAmong(customizationIds, servingSizeCustomizations) ?? DEFAULT_CUSTOMIZATION_ID;
  return {
    servingSizeCustomizationId: isInCart || preselectDefaultServingSize ? servingSizeCustomizationId : undefined,
    spiceLevelCustomizationId:
      customizationIdAmong(customizationIds, spiceLevelCustomizations) ?? DEFAULT_CUSTOMIZATION_ID,
    quantity: isInCart ? quantityInCart : 1,
  };
};

/** The customizations a dish modal form selects, without the shef's defaults, which are not customizations. */
export const customizationIdsFromFormState = ({
  spiceLevelCustomizationId,
  servingSizeCustomizationId,
}: Pick<IBaseDishModalFormState, 'spiceLevelCustomizationId' | 'servingSizeCustomizationId'>): string[] =>
  compact([spiceLevelCustomizationId, servingSizeCustomizationId]).filter(
    (customizationId) => customizationId !== DEFAULT_CUSTOMIZATION_ID
  );
